/**
 * Astrological Wisdom & Synodic Ephemeris Panel
 * Displays real-time live lunar phase, zodiac transits, mansions of the moon,
 * and an interactive synodic scrubber to simulate the sun-moon illumination angle.
 */

import { calculateLunarEphemeris, ZODIAC_SIGNS } from '../data/lunarEphemeris.js';

export class AstrologyPanel {
  constructor(container, onPhaseAngleChange = null) {
    this.container = container;
    this.onPhaseAngleChange = onPhaseAngleChange;

    this.element = document.createElement('div');
    this.element.className = 'side-panel astrology-panel glass-panel hidden';
    this.container.appendChild(this.element);

    this.currentDate = new Date();
    this.render();
  }

  render() {
    const ephemeris = calculateLunarEphemeris(this.currentDate);

    this.element.innerHTML = `
      <div class="panel-header">
        <div class="title-wrap">
          <span class="panel-tag">LUNAR ASTROLOGY</span>
          <h2>Celestial Ephemeris & Transits</h2>
        </div>
        <button class="panel-close-btn" id="astro-close">&times;</button>
      </div>

      <div class="panel-content">
        <!-- Live Current Phase Card -->
        <div class="astro-card highlight-card">
          <div class="phase-header">
            <span class="phase-emoji">${ephemeris.phaseEmoji}</span>
            <div>
              <h3 class="phase-name">${ephemeris.phaseName}</h3>
              <span class="phase-illum">${ephemeris.illuminationPct}% Illuminated • Day ${ephemeris.moonAgeDays} of 29.5</span>
            </div>
          </div>
          <div class="phase-progress-bar">
            <div class="progress-fill" style="width: ${ephemeris.illuminationPct}%"></div>
          </div>
          <div class="phase-meta-row">
            <span>🌕 Full Moon in: <strong>${ephemeris.daysToFull} days</strong></span>
            <span>🌑 New Moon in: <strong>${ephemeris.daysToNew} days</strong></span>
          </div>
        </div>

        <!-- Zodiac Sign Transit -->
        <div class="astro-card">
          <div class="card-title-row">
            <h4>ZODIACAL TRANSIT</h4>
            <span class="zodiac-badge ${ephemeris.zodiacSign.element.toLowerCase()}">
              ${ephemeris.zodiacSign.glyph} ${ephemeris.zodiacSign.name}
            </span>
          </div>
          <p class="zodiac-sub">
            The Moon is transiting <strong>${ephemeris.zodiacSign.name}</strong> at <strong>${ephemeris.signDegrees}</strong>
            (${ephemeris.zodiacSign.element} • ${ephemeris.zodiacSign.modality} • Ruled by ${ephemeris.zodiacSign.ruler}).
          </p>
          <div class="dignity-tag">Dignity: ${ephemeris.zodiacSign.dignity}</div>
          <p class="zodiac-desc"><strong>Archetype:</strong> ${ephemeris.zodiacSign.archetype} — ${ephemeris.zodiacSign.keywords}.</p>
        </div>

        <!-- 28 Lunar Mansions (Nakshatras) -->
        <div class="astro-card">
          <h4>MANSION OF THE MOON (NAKSHATRA)</h4>
          <p class="mansion-name">#${ephemeris.mansion.number} — <strong>${ephemeris.mansion.name}</strong></p>
          <p class="mansion-symbol">Symbol: <em>${ephemeris.mansion.symbol}</em> (${ephemeris.mansion.deg})</p>
          <p class="mansion-meaning">${ephemeris.mansion.meaning}</p>
        </div>

        <!-- Interactive Phase Simulator Slider -->
        <div class="astro-card scrubber-card">
          <h4>SYNODIC CYCLE SIMULATOR</h4>
          <p class="scrubber-desc">Scrub through the 29.53-day cycle to watch the terminator sweep across the lunar surface:</p>
          <input type="range" id="phase-scrubber" min="0" max="29.53" step="0.1" value="${ephemeris.moonAgeDays}" />
          <div class="scrubber-labels">
            <span>🌑 New</span>
            <span>🌓 1st Qtr</span>
            <span>🌕 Full</span>
            <span>🌗 3rd Qtr</span>
            <span>🌑 New</span>
          </div>
          <button class="reset-time-btn" id="btn-reset-time">↺ Sync to Live Real-Time</button>
        </div>

        <!-- Esoteric Lunar Wisdom -->
        <div class="astro-card wisdom-card">
          <h4>THE ESOTERIC MOON</h4>
          <p>
            In traditional and esoteric astrology, the Moon is the celestial mirror that gathers the invisible light of the cosmos and reflects it down onto the Earth. While the Sun rules outward conscious purpose and vitality, the Moon governs the inner sanctuary: the subconscious mind, ancestral memory, oceanic tides, dreams, and emotional instinct.
          </p>
        </div>
      </div>
    `;

    this.initEvents();
  }

  initEvents() {
    this.element.querySelector('#astro-close').addEventListener('click', () => this.hide());

    const scrubber = this.element.querySelector('#phase-scrubber');
    scrubber.addEventListener('input', (e) => {
      const age = parseFloat(e.target.value);
      const angle = (age / 29.530588) * 2 * Math.PI;
      if (this.onPhaseAngleChange) {
        this.onPhaseAngleChange(angle, age);
      }
    });

    this.element.querySelector('#btn-reset-time').addEventListener('click', () => {
      this.currentDate = new Date();
      this.render();
      const ephemeris = calculateLunarEphemeris(this.currentDate);
      if (this.onPhaseAngleChange) {
        this.onPhaseAngleChange(ephemeris.phaseAngle, parseFloat(ephemeris.moonAgeDays));
      }
    });
  }

  toggle() {
    this.element.classList.toggle('hidden');
  }

  show() {
    this.element.classList.remove('hidden');
  }

  hide() {
    this.element.classList.add('hidden');
  }
}
